import Vue from "vue";
import Vuex from "vuex";
import { vuexOidcCreateStoreModule } from "vuex-oidc";
import { oidcSettings } from "../config/oidc";

import {
  FETCH_BOOKS,
  FETCH_SECTIONS,
  SAVE_RECORDING,
  DELETE_RECORDING,
  SET_TARGET,
  UPDATE_TARGET,
  SET_RECORDING_ID_AND_GUID,
  ADD_RECORDING_TO_TARGET,
  ADD_SPELLING_TO_TARGET,
  ADD_TUTOR_LINK_TO_RECORDING
} from "./mutation-types";

import utils from "../services/utils";
import dataService from "../services/dataService";

Vue.use(Vuex);

function findRecordingById(state, sectionId, recordingId) {
  const recordings = state.sectionRecordings[sectionId];
  if (recordings === undefined || recordings.length === 0) return;
  const recording = recordings.find(r => r.id === recordingId);
  if (recording === undefined) return;
  return recording;
}

function findRecordingByGuid(state, sectionId, recordingGuid) {
  const recordings = state.sectionRecordings[sectionId];
  if (recordings === undefined || recordings.length === 0) return;
  const recording = recordings.find(r => r.guid === recordingGuid);
  if (recording === undefined) return;
  return recording;
}

const state = () => ({
  books: [],
  sections: [],
  sectionRecordings: {},
  target: {},
  historicTargets: []
});

const mutations = {
  [FETCH_BOOKS](state, books) {
    // assuming we fetch all books at once
    state.books = books;
    state.books.sort((a, b) => a.sectionCount - b.sectionCount);
  },
  [FETCH_SECTIONS](state, sections) {
    // keep the already fetched sections
    const fetchedSectionIds = state.sections.map(s => s.id);
    state.sections = state.sections.concat(
      sections.filter(s => !fetchedSectionIds.includes(s.id))
    );
  },
  [SAVE_RECORDING](state, { sectionId, recording }) {
    if (state.sectionRecordings[sectionId] === undefined)
      Vue.set(state.sectionRecordings, sectionId, [recording]);
    else if (
      !state.sectionRecordings[sectionId].find(r => r.id === recording.id)
    )
      state.sectionRecordings[sectionId].unshift(recording);
  },
  [SET_RECORDING_ID_AND_GUID](
    state,
    { sectionId, recordingId, newRecordingId, newRecordingGuid }
  ) {
    const recording = findRecordingById(state, sectionId, recordingId);
    recording.id = newRecordingId;
    recording.guid = newRecordingGuid;
  },
  [ADD_TUTOR_LINK_TO_RECORDING](
    state,
    { sectionId, recordingGuid, tutorLink }
  ) {
    const recording = findRecordingByGuid(state, sectionId, recordingGuid);
    recording.tutorLinks.unshift(tutorLink);
  },
  [DELETE_RECORDING](state, { sectionId, recordingId }) {
    const recordings = state.sectionRecordings[sectionId];
    const index = recordings.findIndex(r => r.id === recordingId);
    recordings.splice(index, 1);
  },
  [ADD_RECORDING_TO_TARGET](state, recording) {
    Vue.set(
      state.target,
      "currentDailyRecordings",
      state.target.currentDailyRecordings + 1
    );
    Vue.set(
      state.target,
      "currentDailySeconds",
      state.target.currentDailySeconds + recording.lengthInSeconds
    );
  },
  [ADD_SPELLING_TO_TARGET](state) {
    Vue.set(
      state.target,
      "currentDailySpellings",
      state.target.currentDailySpellings + 1
    );
  },
  [SET_TARGET](state, target) {
    Vue.set(
      state.target,
      "targetDailyRecordings",
      target.targetDailyRecordings
    );
    Vue.set(
      state.target,
      "currentDailyRecordings",
      target.currentDailyRecordings
    );
    Vue.set(state.target, "targetDailySeconds", target.targetDailySeconds);
    Vue.set(state.target, "currentDailySeconds", target.currentDailySeconds);
    Vue.set(state.target, "targetDailySpellings", target.targetDailySpellings);
    Vue.set(
      state.target,
      "currentDailySpellings",
      target.currentDailySpellings
    );
  },
  [UPDATE_TARGET](state, newTarget) {
    Vue.set(
      state.target,
      "targetDailyRecordings",
      newTarget.targetDailyRecordings
    );
    Vue.set(state.target, "targetDailySeconds", newTarget.targetDailySeconds);
    Vue.set(
      state.target,
      "targetDailySpellings",
      newTarget.targetDailySpellings
    );
  }
};

const actions = {
  async fetchBooksAction({ commit, state }) {
    if (state.books.length > 0) return;
    const books = await dataService.getBooks();
    commit(FETCH_BOOKS, books);
  },
  async fetchSectionsAction({ dispatch, commit, state }, { bookId, from, to }) {
    if (state.books.length === 0) await dispatch("fetchBooksAction");
    const book = state.books.find(b => b.id == bookId);
    const sections = await dataService.getSections(bookId, from, to);
    sections.forEach(p => (p.total = book.sectionCount));
    commit(FETCH_SECTIONS, sections);
  },
  async fetchRecordingsAction({ commit }, { sectionId }) {
    const recordings = await dataService.getUserRecordingsForSection(sectionId);
    recordings.forEach(r =>
      commit(SAVE_RECORDING, {
        sectionId: sectionId,
        recording: r
      })
    );
  },
  async uploadAndSaveRecording(
    { commit },
    { sectionId, audioUrl, audioBlob, duration, text, startTime, endTime }
  ) {
    const date = new Date().toISOString();
    const audio = {
      audioUrl: audioUrl,
      blob: audioBlob,
      date: date,
      lengthInSeconds: duration,
      text: text,
      startTime: startTime,
      endTime: endTime,
      tutorLinks: [],
      id: utils.getRandomAudioClass()
    };
    commit(SAVE_RECORDING, {
      sectionId: sectionId,
      recording: audio
    });
    const recording = await dataService.submitRecording(
      audioBlob,
      date,
      duration,
      text,
      startTime,
      endTime,
      sectionId
    );
    commit(SET_RECORDING_ID_AND_GUID, {
      sectionId: sectionId,
      recordingId: audio.id,
      newRecordingId: recording.id,
      newRecordingGuid: recording.guid
    });
    commit(ADD_RECORDING_TO_TARGET, audio);
  },
  async shareRecordingWithTutor(
    { commit },
    { sectionId, recordingGuid, tutorGuid }
  ) {
    const tutorLink = await dataService.shareRecordingWithTutor(
      recordingGuid,
      tutorGuid
    );
    if (!tutorLink) return;
    commit(ADD_TUTOR_LINK_TO_RECORDING, {
      sectionId,
      recordingGuid,
      tutorLink
    });
  },
  async deleteRecording({ commit }, { sectionId, audioId, audioGuid }) {
    await dataService.deleteRecording(audioGuid);
    commit(DELETE_RECORDING, {
      sectionId: sectionId,
      recordingId: audioId
    });
  },
  async fetchTarget({ commit }) {
    const now = utils.getNowDateString();
    const target = await dataService.getTarget(now);
    commit(SET_TARGET, target);
  },
  async updateTarget({ commit }, newTarget) {
    await dataService.updateTarget(newTarget);
    commit(UPDATE_TARGET, newTarget);
  },
  async recordSpelling({ commit }, { bookId, correct }) {
    await dataService.recordSpelling(bookId, correct);
    commit(ADD_SPELLING_TO_TARGET);
  }
};

const getters = {
  // parameterized getters are not cached. so this is just a convenience to get the state.
  getBookById: state => id => state.books.find(b => b.id == id),
  getRecordings: state => sectionId => state.sectionRecordings[sectionId],
  getTarget: state => state.target,
  getHistoricTargets: state => state.historicTargets
};

export default new Vuex.Store({
  strict: process.env.NODE_ENV !== "production",
  state,
  mutations,
  actions,
  getters,
  modules: {
    oidcStore: vuexOidcCreateStoreModule(oidcSettings, {
      isAuthenticatedBy: "access_token"
    })
  }
});
